import { c as _c } from "react/compiler-runtime";
import { getLogger } from '@onefootprint/logger';
import type { DocumentUpload, Entity } from '@onefootprint/request-types/dashboard';
import { DialogPage } from '@onefootprint/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type useEntityVault from '@/entities/hooks/use-entity-vault';
import { useDecryptForm } from '@/entity/components/decrypt-form';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import useDocumentField from '@/entity/hooks/use-document-field';
import type { TransformedDocument } from '@/entity/hooks/use-documents';
import useDocumentsQueryParams from '@/entity/hooks/use-documents-query-params';
import Decrypt from './components/decrypt';
import DecryptedCustomDocumentContent from './components/decrypted-custom-document-content';
import DecryptedDocumentContent from './components/decrypted-document-content';
import DocumentDialogTitle from './components/document-dialog-title';
import { getUploadKey } from './utils/get-doc-upload-key';
const {
  logError
} = getLogger({
  location: 'document-details-dialog'
});
export type DocumentDetailsDialogProps = {
  document: TransformedDocument;
  entity: Entity;
  vault: ReturnType<typeof useEntityVault>;
  seqno?: number;
};
const DocumentDetailsDialog = t0 => {
  const $ = _c(36);
  const {
    document,
    entity,
    vault,
    seqno
  } = t0;
  const {
    t
  } = useTranslation("entity-details");
  const form = useDecryptForm();
  const decryptControls = useDecryptControls();
  let t1;
  if ($[0] !== entity || $[1] !== seqno) {
    t1 = {
      entity,
      seqno
    };
    $[0] = entity;
    $[1] = seqno;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    getProps,
    getRelatedDisByFormValue
  } = useDocumentField(t1);
  let t2;
  if ($[3] !== document || $[4] !== getProps) {
    t2 = getProps(document);
    $[3] = document;
    $[4] = getProps;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    isDecryptable,
    isDecrypted
  } = t2;
  const {
    clear
  } = useDocumentsQueryParams();
  const firstUpload = document.uploads[0];
  let t3;
  if ($[6] !== firstUpload) {
    t3 = firstUpload ? getUploadKey(firstUpload) : "";
    $[6] = firstUpload;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const [selectedUploadKey, setSelectedUploadKey] = useState(t3);
  let t4;
  if ($[8] !== document.uploads || $[9] !== selectedUploadKey) {
    let t5;
    if ($[11] !== selectedUploadKey) {
      t5 = u => getUploadKey(u) === selectedUploadKey;
      $[11] = selectedUploadKey;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    t4 = document.uploads.find(t5);
    $[8] = document.uploads;
    $[9] = selectedUploadKey;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const selectedUpload = t4;
  let t5;
  if ($[13] !== decryptControls || $[14] !== document || $[15] !== entity || $[16] !== form || $[17] !== getRelatedDisByFormValue || $[18] !== t || $[19] !== vault) {
    t5 = async () => {
      if (!vault.data) {
        return;
      }
      const fields = getRelatedDisByFormValue(document);
      form.setFields(fields);
      const response = await decryptControls.decrypt({
        fields,
        entity,
        reason: t(_temp)
      });
      if (response) {
        vault.update({
          vault: response,
          transforms: {},
          dataKinds: {}
        });
      } else {
        logError("Vault decryption error", undefined, {
          entityId: entity.id,
          fields: fields.join(","),
          detail: "No response in DocumentItem Details"
        });
      }
    };
    $[13] = decryptControls;
    $[14] = document;
    $[15] = entity;
    $[16] = form;
    $[17] = getRelatedDisByFormValue;
    $[18] = t;
    $[19] = vault;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const handleDecrypt = t5;
  let t6;
  if ($[21] !== document || $[22] !== selectedUploadKey) {
    t6 = <DocumentDialogTitle document={document} customUploadKey={selectedUploadKey} onChangeUploadKey={setSelectedUploadKey} />;
    $[21] = document;
    $[22] = selectedUploadKey;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== decryptControls || $[25] !== document || $[26] !== handleDecrypt || $[27] !== isDecryptable || $[28] !== isDecrypted || $[29] !== selectedUpload || $[30] !== vault) {
    t7 = isDecrypted ? <DecryptedContent document={document} vault={vault} selectedCustomUpload={selectedUpload} /> : <Decrypt isDecryptable={isDecryptable} onClick={handleDecrypt} isDecrypting={decryptControls.isDecrypting} />;
    $[24] = decryptControls;
    $[25] = document;
    $[26] = handleDecrypt;
    $[27] = isDecryptable;
    $[28] = isDecrypted;
    $[29] = selectedUpload;
    $[30] = vault;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] !== clear || $[33] !== t6 || $[34] !== t7) {
    t8 = <DialogPage title={t6} noPadding={true} noScroll={true} onClose={clear}>{t7}</DialogPage>;
    $[32] = clear;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  return t8;
};
const DecryptedContent = t0 => {
  const $ = _c(4);
  const {
    document,
    vault,
    selectedCustomUpload
  } = t0;
  let t1;
  if ($[0] !== document || $[1] !== selectedCustomUpload || $[2] !== vault) {
    t1 = document.kind === "custom" ? <DecryptedCustomDocumentContent document={document} vault={vault} upload={selectedCustomUpload} /> : <DecryptedDocumentContent document={document} vault={vault} />;
    $[0] = document;
    $[1] = selectedCustomUpload;
    $[2] = vault;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export default DocumentDetailsDialog;
function _temp($) {
  return $.decrypt.documentDetailsReason;
}